import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { mediaUrl, track } from "@/lib/api";
import { Reveal, SectionHead, Counter } from "@/components/Reveal";
import Timeline from "@/components/Timeline";

export default function About() {
  const { settings } = useSite();
  const about = settings?.about || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start center", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const isWa = String(settings?.cta_banner?.button_href || "").startsWith("whatsapp:");
  const ctaHref = isWa
    ? `https://wa.me/${String(settings?.contact?.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(settings?.templates?.general || "Hi!")}`
    : settings?.cta_banner?.button_href || "/contact";

  return (
    <div className="pt-24 sm:pt-28" data-testid="about-page">
      <section ref={ref} className="relative py-14 sm:py-20 overflow-hidden">
        <div className="ambient-glow w-[560px] h-[320px] -top-32 right-0 opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="font-mono-label text-[11px] text-accent-bright mb-5">The Studio</p>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.1] mb-8" data-testid="about-heading">
                  {about.heading || "About us"}
                </h1>
              </Reveal>
              <div className="space-y-5">
                {(about.story || []).map((para, i) => (
                  <Reveal key={i} delay={i * 0.08}>
                    <p className="text-sm sm:text-base text-mist leading-relaxed" data-testid={`about-story-${i}`}>{para}</p>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <motion.div style={{ y: imgY }}>
                <Reveal delay={0.15}>
                  <div className="spot-frame aspect-[4/5]" data-testid="about-image-frame">
                    <img src={mediaUrl(about.image_url)} alt="Inside the studio" loading="lazy" />
                  </div>
                </Reveal>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-[#131327]/40 border-y border-white/[0.06]" data-testid="about-stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {(about.stats || []).map((s, i) => (
              <Reveal key={i} delay={i * 0.07} className="text-center">
                <div className="glass rounded-3xl p-8 sm:p-10" data-testid={`about-stat-card-${i}`}>
                  <p className="font-display text-4xl sm:text-5xl font-bold text-grad" data-testid={`about-stat-value-${i}`}>
                    <Counter value={Number(s.value) || 0} suffix={s.suffix || ""} />
                  </p>
                  <p className="mt-3 font-mono-label text-[10px] text-mist">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="about-values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Core Values" title="What we refuse to compromise" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {(about.values || []).map((v, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="h-full glass rounded-2xl p-6 sm:p-7 transition-all duration-500 hover:border-accent/40 hover:-translate-y-1" data-testid={`about-value-card-${i}`}>
                  <span className="font-mono-label text-[10px] text-accent-bright">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-lg font-semibold text-white mt-4 mb-2.5">{v.title}</h3>
                  <p className="text-sm text-mist leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#131327]/40 border-y border-white/[0.06]" data-testid="about-process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="How We Work" title={settings?.process?.heading || "Our Work Process"} sub={settings?.process?.sub} />
          <Timeline steps={settings?.process?.steps} />
        </div>
      </section>

      <section className="relative py-20 sm:py-24 overflow-hidden">
        <div className="ambient-glow w-[600px] h-[300px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-60" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-5">{settings?.cta_banner?.heading}</h2>
            <a
              href={ctaHref}
              target={isWa ? "_blank" : undefined}
              rel="noopener noreferrer"
              onClick={() => track("button_click", "/about-cta")}
              className="inline-flex items-center gap-2.5 rounded-full bg-accent hover:bg-accent-bright text-white font-semibold px-8 py-4 shadow-[0_0_36px_rgba(124,58,237,0.5)] transition-all duration-300 hover:-translate-y-1"
              data-testid="about-cta-button"
            >
              {settings?.cta_banner?.button_label} <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
